import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Heart, Users, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import useOverlayGate from '../../hooks/useOverlayGate';

const STORAGE_KEY = 'hoibuur:first_time_popup_shown';

/**
 * Shown once per logged-in user on their first visit to the homepage.
 *
 * Trigger: user is logged-in AND localStorage key is missing. Dismissal
 * persists the key so the dialog never reappears. Wacht op de overlay-gate
 * zodat andere pop-ups niet tegelijk in beeld springen.
 */
const FirstTimeOrganizerDialog = () => {
  const { user } = useAuth();
  const [wantOpen, setWantOpen] = useState(false);
  const gate = useOverlayGate('first-time-organizer', 60);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setWantOpen(true), 800);
    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    if (wantOpen) gate.request();
    else gate.release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantOpen]);

  const open = wantOpen && gate.canShow;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setWantOpen(false);
    gate.release();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="sm:max-w-md rounded-3xl p-0 overflow-hidden border-0"
        data-testid="first-time-organizer-dialog"
      >
        <div className="bg-gradient-to-br from-hoi-coral/20 via-hoi-yellow/20 to-white px-6 pt-8 pb-6 text-center relative">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Sluiten"
            data-testid="first-time-organizer-close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
          <div className="w-14 h-14 rounded-full bg-white shadow-md mx-auto mb-4 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-hoi-coral" aria-hidden="true" />
          </div>
          <DialogTitle className="font-heading text-2xl text-primary mb-2">
            Welkom, {user.name?.split(' ')[0] || 'buur'}!
          </DialogTitle>
          <DialogDescription className="text-base text-gray-700">
            Hoi Buur is niet alleen om te kijken, het is van én voor de buurt.
          </DialogDescription>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-hoi-blue/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-hoi-blue" aria-hidden="true" />
              </div>
              <span className="text-sm text-gray-700 leading-relaxed pt-1">
                <strong>Ontmoet je buren</strong> via activiteiten in de buurt.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-hoi-coral/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-4 h-4 text-hoi-coral" aria-hidden="true" />
              </div>
              <span className="text-sm text-gray-700 leading-relaxed pt-1">
                <strong>Doe mee</strong> of <strong>organiseer zelf</strong>: een koffie, wandeling of klusclub.
              </span>
            </li>
          </ul>

          <Link to="/activiteit-toevoegen" onClick={dismiss} className="block">
            <Button
              className="w-full rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-white font-heading py-3 shadow-md shadow-hoi-coral/25"
              data-testid="first-time-organizer-cta"
            >
              <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Plaats je eerste activiteit
            </Button>
          </Link>
          <button
            onClick={dismiss}
            className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
            data-testid="first-time-organizer-skip"
          >
            Eerst rondkijken
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstTimeOrganizerDialog;
